import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientId } from '../hooks/useClientId';
import { useActivities, useActivitySummary } from '../hooks/useActivities';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { formatDate } from '../format';
import type { Activity, ActivityType } from '../value.api';

const ACTIVITY_CONFIG: Record<ActivityType, { icon: string; color: string }> = {
  product_launched: { icon: 'rocket_launch', color: 'var(--success-dark)' },
  change_processed: { icon: 'edit_note', color: 'var(--secondary)' },
  retailer_question: { icon: 'support_agent', color: 'var(--primary)' },
  gs1_update: { icon: 'update', color: 'var(--secondary)' },
  legislation_change: { icon: 'gavel', color: 'var(--warning)' },
  packaging_update: { icon: 'package_2', color: 'var(--secondary)' },
};

const PERIODS = ['month', 'quarter', 'year'] as const;

export function ActivitiesPage() {
  const { t, i18n } = useTranslation();
  const clientId = useClientId();

  const [period, setPeriod] = useState<string>('month');
  const [allActivities, setAllActivities] = useState<Activity[]>([]);
  const [page, setPage] = useState(1);

  const { data: summaryData, isLoading: summaryLoading } = useActivitySummary(clientId, period);
  const { data: activitiesData, isLoading, isError, error, refetch } = useActivities(clientId, { period, page, pageSize: 10 });

  // Accumulate activities across pages for load-more
  const currentActivities = activitiesData?.data ?? [];
  const displayActivities = page === 1 ? currentActivities : [...allActivities, ...currentActivities];

  const handleLoadMore = useCallback(() => {
    setAllActivities(displayActivities);
    setPage((p) => p + 1);
  }, [displayActivities]);

  const handlePeriodChange = useCallback((p: string) => {
    setPeriod(p);
    setPage(1);
    setAllActivities([]);
  }, []);

  if (isError) {
    return <ApiErrorMessage error={error} onRetry={() => refetch()} />;
  }

  const summary = summaryData?.data;
  const meta = activitiesData?.meta;
  const hasMore = meta ? page * meta.pageSize < meta.total : false;

  const summaryCards = [
    { labelKey: 'activities.processed', value: summary?.product_launched ?? 0, icon: 'inventory_2', color: 'var(--primary)' },
    { labelKey: 'activities.changes', value: (summary?.change_processed ?? 0) + (summary?.packaging_update ?? 0), icon: 'edit_note', color: 'var(--secondary)' },
    { labelKey: 'activities.retailerQuestions', value: summary?.retailer_question ?? 0, icon: 'support_agent', color: 'var(--primary)' },
    { labelKey: 'activities.gs1Updates', value: summary?.gs1_update ?? 0, icon: 'update', color: 'var(--secondary)' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--foreground-heading)' }}>
            {t('activities.title')}
          </h1>
          <p className="mt-[3px]" style={{ fontSize: '0.8125rem', color: 'var(--foreground-muted)' }}>
            {t('activities.subtitle')}
          </p>
        </div>

        {/* Period toggle with ARIA */}
        <div role="tablist" aria-label={t('activities.periodLabel', 'Period')} className="flex gap-1 rounded-lg p-1" style={{ backgroundColor: 'var(--background-muted)' }}>
          {PERIODS.map((p) => (
            <button
              key={p}
              role="tab"
              aria-selected={period === p}
              onClick={() => handlePeriodChange(p)}
              className="rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                backgroundColor: period === p ? 'var(--background)' : 'transparent',
                color: period === p ? 'var(--primary)' : 'var(--foreground-muted)',
                boxShadow: period === p ? 'var(--card-shadow)' : 'none',
              }}
            >
              {t(`activities.period.${p}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {(isLoading || summaryLoading) ? (
          Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)
        ) : (
          summaryCards.map((card) => (
            <div
              key={card.labelKey}
              className="flex items-center gap-3 rounded-xl px-4 py-3"
              style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)', boxShadow: 'var(--card-shadow)' }}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--primary-light)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', color: card.color }}>{card.icon}</span>
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>{card.value}</p>
                <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{t(card.labelKey)}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Timeline */}
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <div className="skeleton h-8 w-8 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-3 w-48 rounded" />
                <div className="skeleton h-2.5 w-full rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : displayActivities.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-center" style={{ color: 'var(--foreground-muted)' }}>
          <span className="material-symbols-outlined mb-3" style={{ fontSize: '48px' }}>history</span>
          <p className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>{t('activities.empty')}</p>
          <p className="mt-1 text-sm">{t('activities.emptyDescription')}</p>
        </div>
      ) : (
        <div className="space-y-0">
          {displayActivities.map((activity) => (
            <ActivityTimelineItem key={activity.id} activity={activity} locale={i18n.language} t={t} />
          ))}
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center">
          <button
            onClick={handleLoadMore}
            className="rounded-lg px-4 py-2 text-sm font-medium"
            style={{ backgroundColor: 'var(--primary-light)', color: 'var(--primary)' }}
          >
            {t('activities.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}

function ActivityTimelineItem({ activity, locale, t }: { activity: Activity; locale: string; t: (key: string) => string }) {
  const config = ACTIVITY_CONFIG[activity.type] ?? { icon: 'circle', color: 'var(--foreground-muted)' };

  return (
    <div className="flex gap-3 py-3" style={{ borderBottom: '1px solid var(--border-soft)' }}>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: 'var(--primary-light)' }}>
        <span className="material-symbols-outlined" style={{ fontSize: '16px', color: config.color }}>{config.icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[0.8125rem] font-medium truncate" style={{ color: 'var(--foreground)' }}>{activity.title}</p>
          <span className="shrink-0 rounded px-1.5 py-0.5 text-[0.625rem] font-medium" style={{ backgroundColor: 'var(--primary-light)', color: 'var(--primary)' }}>
            {t(`activities.types.${activity.type}`)}
          </span>
        </div>
        {activity.description && (
          <p className="mt-0.5 text-xs" style={{ color: 'var(--foreground-muted)' }}>{activity.description}</p>
        )}
        {activity.productGtin && (
          <p className="mt-0.5 text-xs font-mono" style={{ color: 'var(--foreground-muted)' }}>GTIN: {activity.productGtin}</p>
        )}
        <p className="mt-1 text-[0.6875rem]" style={{ color: 'var(--foreground-muted)' }}>{formatDate(activity.createdAt, locale)}</p>
      </div>
    </div>
  );
}
